import ChevronRight from '@ixigo/icons/ChevronRight';
import ButtonBase from '@ixigo/iui/ButtonBase';
import Collapse from '@ixigo/iui/Collapse';
import { useState } from 'react';
import type { LabelIcon, RefundBreakUp, RefundSummaryItem } from '@/api';
import { FareItemGroup, LineItem } from '@/components/fare-summary';
import { DynamicIcon, type IconModule } from '@/components/lazy-icons';
import { SectionHeader } from '@/components/section-header';

const LABEL_ICON_MAP: Record<LabelIcon, () => Promise<IconModule>> = {
	IxigoMoney: () => import('@ixigo/icons/IxigoMoneyFilled'),
	IxigoMoneyFilled: () => import('@ixigo/icons/IxigoMoneyFilled'),
	WalletFilled: () => import('@ixigo/icons/WalletFilled'),
	BankFilled: () => import('@ixigo/icons/BankFilled'),
	CreditCard: () => import('@ixigo/icons/CreditCard'),
};

const ICON_COLOR_MAP: Record<LabelIcon, string> = {
	IxigoMoney: 'text-brand',
	IxigoMoneyFilled: 'text-brand',
	WalletFilled: 'text-success',
	BankFilled: 'text-success',
	CreditCard: 'text-success',
};

export function RefundSummary({
	refundSummary,
}: {
	refundSummary: RefundBreakUp;
}) {
	const [isExpanded, setIsExpanded] = useState(true);
	const [showCollapsedItems, setShowCollapsedItems] = useState(false);

	const collapsedItems = [
		refundSummary?.charges[0],
		refundSummary?.deductions[0],
	].filter((item): item is RefundSummaryItem => item !== undefined);

	return (
		<div className="bg-primary p-20 flex flex-col gap-y-15" id="refund-details">
			<ButtonBase
				className="flex items-center justify-between"
				onClick={() => setIsExpanded(!isExpanded)}
				disableRipple
			>
				<SectionHeader title="Refund Details" />
				<ChevronRight
					className={`transition-transform duration-300 ${
						isExpanded ? '-rotate-90' : 'rotate-90'
					}`}
				/>
			</ButtonBase>
			<div className="flex flex-col">
				<div className="flex flex-col pb-10">
					{refundSummary.netPay.map((item) => (
						<LineItem
							key={item.label.text}
							label={item.label.text}
							value={item.value.text}
							{...(item.label.color && {
								labelColor: item.label.color,
							})}
							{...(item.value.color && {
								valueColor: item.value.color,
							})}
							size="lg"
						/>
					))}
				</div>
				<Collapse
					in={isExpanded}
					onExit={() => setShowCollapsedItems(true)}
					onEnter={() => setShowCollapsedItems(false)}
				>
					<FareItemGroup className="py-10">
						{refundSummary.charges.map((item, index) => (
							<LineItem
								key={item.label.text}
								label={item.label.text}
								value={item.value.text}
								{...(index === 0 && {
									labelWeight: 'medium',
									valueWeight: 'medium',
								})}
								{...(item.label.color && {
									labelColor: item.label.color,
								})}
								{...(item.value.color && {
									valueColor: item.value.color,
								})}
							/>
						))}
					</FareItemGroup>
				</Collapse>
				<Collapse in={isExpanded && refundSummary.discounts.length > 0}>
					<FareItemGroup className="py-10">
						{refundSummary.discounts.map((item) => (
							<LineItem
								key={item.label.text}
								label={item.label.text}
								value={item.value.text}
								{...(item.label.color && {
									labelColor: item.label.color,
								})}
								{...(item.value.color && {
									valueColor: item.value.color,
								})}
								{...(item.label.icon && {
									rightIcon: (
										<DynamicIcon
											iconName={item.label.icon}
											iconImportMap={LABEL_ICON_MAP}
											{...(item.label.icon in ICON_COLOR_MAP && {
												className: ICON_COLOR_MAP[item.label.icon],
											})}
										/>
									),
								})}
							/>
						))}
					</FareItemGroup>
				</Collapse>
				<Collapse in={isExpanded && refundSummary.deductions.length > 0}>
					<FareItemGroup className="py-10">
						{refundSummary.deductions.map((item, index) => (
							<LineItem
								key={item.label.text}
								label={item.label.text}
								value={item.value.text}
								{...(index === 0 && {
									labelWeight: 'medium',
									valueWeight: 'medium',
								})}
								{...(item.label.color && {
									labelColor: item.label.color,
								})}
								{...(item.value.color && {
									valueColor: item.value.color,
								})}
							/>
						))}
					</FareItemGroup>
				</Collapse>
				<Collapse in={showCollapsedItems}>
					<FareItemGroup>
						{collapsedItems?.map((item) => (
							<LineItem
								key={item?.label.text}
								label={item.label.text}
								value={item.value.text}
								{...(item.label.color && { labelColor: item.label.color })}
								{...(item.value.color && { valueColor: item.value.color })}
							/>
						))}
					</FareItemGroup>
				</Collapse>
			</div>
		</div>
	);
}
